import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Grid,
    Chip,
    Link,
    Stack
} from '@mui/material';
import { Appointment } from '../../types/appointment';
import { OpenInNew } from '@mui/icons-material';

interface SpreadInfo {
    id: string;
    title: string;
    points: number;
}

interface AppointmentDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    appointment: Appointment | null;
    spreadInfo?: SpreadInfo;
}

export const AppointmentDetailsDialog: React.FC<AppointmentDetailsDialogProps> = ({
    open,
    onClose,
    appointment,
    spreadInfo
}) => {
    if (!appointment) return null;

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending': return 'Ödeme Bekliyor';
            case 'confirmed': return 'Onaylandı';
            case 'completed': return 'Tamamlandı';
            case 'rejected': return 'Reddedildi';
            default: return 'Bilinmiyor';
        }
    };

    const getStatusChipColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' => {
        switch (status) {
            case 'pending': return 'warning';
            case 'confirmed': return 'info';
            case 'completed': return 'success';
            case 'rejected': return 'error';
            default: return 'default';
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6">Randevu Detayları</Typography>
                    <Chip
                        label={getStatusText(appointment.status)}
                        color={getStatusChipColor(appointment.status)}
                        size="small"
                    />
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                            Danışan Bilgileri
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {appointment.userName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {appointment.userEmail}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            {appointment.userPhone}
                        </Typography>

                        <Box mt={2}>
                            <Typography variant="subtitle2" color="textSecondary">
                                Randevu Zamanı
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                {appointment.date} - {appointment.time}
                            </Typography>
                        </Box>

                        <Box mt={2}>
                            <Typography variant="subtitle2" color="textSecondary">
                                Fal Bilgileri
                            </Typography>
                            <Typography variant="body1">
                                Fal Türü: {spreadInfo?.title || 'Bilinmiyor'}
                            </Typography>
                            {spreadInfo && (
                                <Typography variant="body2" color="textSecondary">
                                    Puan: {spreadInfo.points}
                                </Typography>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                            Danışanın Sorusu/Açıklaması
                        </Typography>
                        <Typography variant="body1" paragraph>
                            {appointment.notes || 'Açıklama belirtilmemiş'}
                        </Typography>

                        {appointment.receiptUrl && (
                            <Box mt={2}>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    Ödeme Dekontu
                                </Typography>
                                <Link 
                                    href={appointment.receiptUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                    Görüntüle <OpenInNew sx={{ ml: 0.5, fontSize: 16 }} />
                                </Link>
                            </Box>
                        )}

                        {appointment.rejectionReason && (
                            <Box mt={2}>
                                <Typography variant="subtitle2" color="error">
                                    Red Nedeni
                                </Typography>
                                <Typography variant="body1" color="error">
                                    {appointment.rejectionReason}
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Kapat
                </Button>
            </DialogActions>
        </Dialog>
    );
}; 